import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material'
import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CustomExplain } from '../components/CustomExplain'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { loadStatus } from '../constants/loadStatus'
import { confirmMsg, errorMsg } from '../constants/messages'
import {
  PasswordResetErrorIntervalMinutes,
  PasswordResetErrorLimitTimes,
} from '../constants/passwordResetLimit'
import { useAuth } from '../hooks/useAuth'
import { useCA01EmailVerification } from '../hooks/useCA01EmailVerification'
import { useCA02EmailPasswordReset } from '../hooks/useCA02EmailPasswordReset'
import { ReactComponent as CotopatImage } from '../images/cotopat-logo.svg'
import { emailSchema } from '../schema/emailSchema'
import { LanguageContext } from '../util/LanguageContext'
import { VersionContext } from '../util/VersionContext'

interface EmailEntryProps {
  passwordReset: boolean
}
// メールアドレス入力画面
export const EmailEntry: React.FC<EmailEntryProps> = props => {
  // hooks
  const auth = useAuth()
  // 文字列変換用
  const translation = useContext(LanguageContext)
  // const c102 = useC102UserUpdate();
  // const c107 = useC107EmailGet();
  const ca01 = useCA01EmailVerification()
  const ca02 = useCA02EmailPasswordReset()

  // ダイアログ
  const [open, setOpen] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  // 認証loading state
  const [email, setEmail] = useState('')

  const [isDisabled, setIsDisabled] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const { version } = useContext(VersionContext)

  const validate = async (value: string) => {
    try {
      await emailSchema.validate(value)
      setErrorMessage('')
      setIsDisabled(false)
    } catch (err: any) {
      setErrorMessage(err.message)
      setIsDisabled(true)
    }
  }

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    validate(event.target.value)
  }
  const navigate = useNavigate()
  const onClickCancel = () => {
    navigate('/login')
  }

  const onClickEmailEntry = async () => {
    // 実行回数変数
    const executeCntName = 'Count'
    // 実行時刻変数
    const executeTimeName = 'Time'
    // ローカルストレージから実行回数と実行時刻を取得する
    const ExecuteCnt = localStorage.getItem(executeCntName)
    const ExecuteTime = localStorage.getItem(executeTimeName)
    let count = 0
    const currentTime: number = Date.now()
    if (ExecuteCnt) {
      count = Number.parseInt(ExecuteCnt, 10)
    }
    if (ExecuteTime) {
      const savedTime: number = Number.parseInt(ExecuteTime, 10)
      const thresholdTime: number = savedTime + PasswordResetErrorIntervalMinutes * 60 * 1000
      if (currentTime > thresholdTime) {
        count = 0
      }
    }
    if (count >= PasswordResetErrorLimitTimes) {
      setErrorDialog('label_reached_maximum')
      setOpenDialog(true)
      setIsDisabled(false)
      return
    }
    if (props.passwordReset) {
      // パスワードリセットのルート
      // パスワードリセットメール送信
      const result = await ca02.execEmailPasswordReset(email)
      if (result.success) {
        // 制限チェックがあれば削除する
        if (ExecuteCnt) {
          localStorage.removeItem(executeCntName)
        }
        if (ExecuteTime) {
          localStorage.removeItem(executeTimeName)
        }
        // console.log("execEmailPasswordReset succeed");
        // ダイアログオープン
        setOpen(true)
      } else {
        count++
        localStorage.setItem(executeCntName, count.toString())
        localStorage.setItem(executeTimeName, currentTime.toString())
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
      // メールアドレスチェック
      // const resultEmail = await c107.execEmailGet(email);
      // if (!!!resultEmail.success) {
      //   setErrorDialog(resultEmail.message!);
      //   setOpenDialog(true);
      //   return;
      // }
      // メールアドレス認証
      // const result = await auth.sendEmailVerification(
      //   email,
      //   passwordResetStatus.RESET_ON
      // );
    } else {
      // メールアドレス確認メール送信
      const resultEmail = await ca01.execEmailVerification(email)
      if (resultEmail.success) {
        // 制限チェックがあれば削除する
        if (ExecuteCnt) {
          localStorage.removeItem(executeCntName)
        }
        if (ExecuteTime) {
          localStorage.removeItem(executeTimeName)
        }
        // console.log("execEmailVerification succeed");
        // ダイアログオープン
        setOpen(true)
      } else {
        setErrorDialog(resultEmail.message!)
        setOpenDialog(true)
      }
      // // ユーザー更新（メールアドレス）
      // const result = await c102.execUserUpdate(auth.loginId, email);
      // if (result.success) {
    }
  }
  const onClickHandler = async () => {
    navigate('/login')
  }
  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  return (
    <>
      {open && (
        <ModalConfirmDialog open={open} text={confirmMsg.EMAIL_SENDED} onClick={onClickHandler} />
      )}
      {openDialog && (
        <ModalConfirmDialog open={openDialog} text={errorDialog} onClick={handleDialogClose} />
      )}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
        }}
      >
        {(ca01.loading === loadStatus.LOADING ||
          ca02.loading === loadStatus.LOADING ||
          auth.loading === loadStatus.LOADING) && <CircularProgress />}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'auto',
          marginTop: '80px',
        }}
      >
        <Paper
          elevation={5}
          sx={{
            width: '787px',
            boxShadow: '0px 2px 2px #00000026',
            borderRadius: '10px',
          }}
        >
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Stack direction="column">
              <Box mt="111px" display="flex" justifyContent="center">
                <CotopatImage width="172px" height="44px" />
              </Box>
              <Typography
                align="center"
                sx={{
                  mt: '80px',
                  mr: '100px',
                  ml: '100px',
                  top: '315px',
                  left: '759px',
                  font: 'normal normal normal 21px/29px Noto Sans JP',
                }}
              >
                <CustomExplain>{translation['message_reset_password_email']}</CustomExplain>
              </Typography>
              <Box display="flex" justifyContent="center">
                <Typography
                  sx={{
                    mt: '30px',
                    top: '315px',
                    left: '759px',
                    font: 'normal normal normal 21px/29px Noto Sans JP',
                  }}
                >
                  {translation['label_email']}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center">
                <TextField
                  variant="outlined"
                  value={email}
                  error={!!errorMessage}
                  helperText={errorMessage ? translation[errorMessage] : ' '}
                  onChange={handleEmailChange}
                  InputProps={{
                    sx: {
                      background: '#F4F4F4',
                    },
                  }}
                  sx={{
                    mt: '8px',
                    width: '402px',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': {
                      // "& fieldset": {
                      //   borderColor: "white",
                      // },
                      // "&:hover fieldset": {
                      //   borderColor: "white",
                      // },
                      '&.Mui-focused fieldset': {
                        borderColor: errorMessage ? 'red' : '#282828',
                      },
                    },
                  }}
                />
              </Box>
            </Stack>
            <Box mt={7} display="flex" gap="40px" marginBottom="50px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{
                  width: '184px',
                  height: '49px',
                  border: '2px solid #282828',
                  borderRadius: '10px',
                  boxShadow: 0,
                  textAlign: 'center',
                  font: 'normal normal bold 21px/29px Noto Sans CJK JP',
                  letterSpacing: '0px',
                  color: '#282828',
                  '&:hover': {
                    background: '#282828 0% 0% no-repeat padding-box',
                    opacity: 0.8,
                    boxShadow: 0,
                  },
                }}
                style={{ backgroundColor: '#F4F4F4' }}
                onClick={onClickCancel}
              >
                {translation['button_cancel']}
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{
                  width: '184px',
                  height: '49px',
                  borderRadius: '10px',
                  boxShadow: 0,
                  textAlign: 'center',
                  font: 'normal normal bold 21px/29px Noto Sans CJK JP',
                  letterSpacing: '0px',
                  color: '#FFFFFF',
                  '&:hover': {
                    background: '#282828 0% 0% no-repeat padding-box',
                    opacity: 0.8,
                    boxShadow: 0,
                  },
                }}
                disabled={isDisabled}
                style={{
                  backgroundColor: isDisabled ? '#F4F4F4' : '#282828',
                }}
                onClick={onClickEmailEntry}
              >
                {translation['button_send']}
              </Button>
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center" mb="30px">
              <Typography
                mb="2px"
                sx={{
                  font: 'normal normal normal 16px/24px Noto Sans JP',
                  textAlign: 'left',
                  letterSpacing: '0px',
                  color: '#282828',
                }}
              >
                {translation['copyright_info']}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          font: 'normal normal normal 12px/12px Noto Sans JP',
          color: '#999999',
        }}
      >
        {version ? `${translation['app_version']}`.replace('{version}', version) : ''}
      </Box>
    </>
  )
}
